import { useState, useEffect } from "react";
import "./App.css";

// COMPONENTS
import BirthDatePicker from "./UIComponents/BirthDatePicker.jsx";
import TextInput from "./UIComponents/Default/TextInput.jsx";
import GenderSelector from "./UIComponents/GenderSelector.jsx";
import PhoneInput from "./UIComponents/PhoneInput.jsx";
import JobsList from "./UIComponents/JobsList.jsx";
import CitySelect from "./UIComponents/CityList.jsx";
import TaxStatusSelect from "./UIComponents/TaxStatusSelect.jsx";
import PhotoUploader from "./UIComponents/PhotoUploader.jsx";
import AdditionalQuestions from "./UIComponents/AdditionalQuestions.jsx";

// STYLES
const inputClass =
  "w-full mb-4 border p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const inputNumberClass =
  "w-full mb-4 border p-2 rounded focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500 transition";
const inputLabelClass = "mb-1 text-left font-medium text-black";

function App() {
  const [fields, setFields] = useState({
    lastName: "",
    firstName: "",
    middleName: "",
    birthDate: "",
    gender: "",
    phone: "",
    selectedJobs: [],
    selectedCity: [],
    taxStatus: "",
    selected: [],
    file: null,
  });

  const [errors, setErrors] = useState({});
  const [telegramId, setTelegramId] = useState(null);
  const [serverUrl, setServerUrl] = useState(null);
  const [notification, setNotification] = useState(""); // <-- уведомление

  // ✅ Получаем Telegram ID
  useEffect(() => {
    if (!window.Telegram || !window.Telegram.WebApp) {
      console.warn("Telegram WebApp не найден.");
      return;
    }

    const tg = window.Telegram.WebApp;

    tg.ready();
    
    if (tg.initDataUnsafe?.user) {
      setTelegramId(tg.initDataUnsafe.user.id);
      console.log("Telegram user found");
    } else {
      console.warn("Telegram user not found, actions cannot be performed");
      setNotification("❌Данные не могут быть сохранены")
    }
  }, []);

  // ✅ Загружаем данные пользователя
  useEffect(() => {
    if (!telegramId) return;

    (async () => {
      try {
        const res = await fetch(`https://botiambtl.ru/api/get-user-data/${telegramId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        console.log("User data:", data);

        const parseDate = (str) => {
          if (!str) return "";
          const [d, m, y] = str.split(".").map(Number);
          const date = new Date(y, m - 1, d);
          return isNaN(date.getTime()) ? "" : date;
        };

        const parseJSONB = (v) => {
          if (!v) return [];
          try {
            const arr = typeof v === "string" ? JSON.parse(v) : v;
            return Array.isArray(arr) ? arr : [];
          } catch {
            return [];
          }
        };

        setFields({
          lastName: data.surname || "",
          firstName: data.name || "",
          middleName: data.lastname || "",
          birthDate: parseDate(data.birthdate),
          gender: data.sex || "",
          phone: data.phone || "",
          selectedJobs: parseJSONB(data.jobs),
          selectedCity: parseJSONB(data.cities),
          taxStatus: data.status ? data.status.replace(/_/g, " ") : "",
          file: data.image_link || null,
          selected: data.has_medical ? { has_med_book: true } : {},
        });
      } catch (err) {
        console.error("Failed to fetch user data:", err);
      }
    })();
  }, [telegramId]);

  // ✅ Валидация поля
  const validateField = (field, value) => {
    const required = ["lastName", "firstName", "birthDate", "gender", "phone", "selectedJobs", "selectedCity", "taxStatus"];
    if (!required.includes(field)) return "";

    if (
      value === "" ||
      value === null ||
      (Array.isArray(value) && value.length === 0)
    ) {
      const names = {
        lastName: "фамилию",
        firstName: "имя",
        birthDate: "дату рождения",
        gender: "пол",
        phone: "телефон",
        selectedJobs: "профессию",
        selectedCity: "город",
        taxStatus: "налоговый статус",
      };

      // Для остальных стандартное сообщение
      return `Введите ${names[field] || "значение"}`;
    }

    return "";
  };

  // ✅ Универсальный setField с валидацией
  const setField = (field, value) => {
    setFields((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const err = validateField(field, value);
      const newErrors = { ...prev };
      if (err) newErrors[field] = err;
      else delete newErrors[field];
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Валидация
    const newErrors = {};
    for (const [key, val] of Object.entries(fields)) {
      const err = validateField(key, val);
      if (err) newErrors[key] = err;
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      let birthDateStr = "";
      if (fields.birthDate instanceof Date) {
        const d = String(fields.birthDate.getDate()).padStart(2, "0");
        const m = String(fields.birthDate.getMonth() + 1).padStart(2, "0");
        const y = fields.birthDate.getFullYear();
        birthDateStr = `${d}.${m}.${y}`;
      } else if (typeof fields.birthDate === "string") {
        birthDateStr = fields.birthDate;
      }
      const payload = {
        user_id: telegramId,
        lastName: fields.lastName,
        firstName: fields.firstName,
        middleName: fields.middleName,
        birthDate: birthDateStr,
        gender: fields.gender,
        phone: fields.phone,
        selectedJobs: fields.selectedJobs,
        selectedCity: fields.selectedCity,
        taxStatus: fields.taxStatus,
        file: fields.file || "",
        selected: fields.selected, // { has_med_book: true } или {}
      };

      const res = await fetch("https://botiambtl.ru/api/save-user-data/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await res.json();

      if (!res.ok) {
        setNotification("❌ Ошибка при сохранении данных");
        console.error("Error saving user data:", result);
        return;
      }

      setNotification("✅ Данные успешно сохранены!");

      try {
        const inviteRes = await fetch(`https://botiambtl.ru/api/invite-link/${telegramId}`, {
          method: "POST",
        });

        const inviteData = await inviteRes.json();
        console.log("Invite response:", inviteData);

        if (inviteData.status === "sent") {
          console.log("Invite link sent to Telegram ✅");
        } else if (inviteData.status === "already_in_group") {
          console.log("User is already in group 👌");
        } else {
          console.warn("Invite error:", inviteData);
        }
      } catch (inviteErr) {
        console.error("Error sending invite:", inviteErr);
      }
      

      // Скрываем уведомление через 3 секунды
      setTimeout(() => setNotification(""), 3000);
    } catch (err) {
      setNotification("❌ Ошибка сети при сохранении данных");
      console.error("Network error:", err);
    }
  };

  return (
    <form className="flex flex-col max-w-md mx-auto bg-white p-8 text-center" onSubmit={handleSubmit}>
      <Field label="Фамилия" error={errors.lastName}>
        <TextInput
          placeholder="Фамилия"
          inputClass={`${inputClass} ${errors.lastName ? "border-red-500" : ""}`}
          value={fields.lastName}
          onChange={(e) => setField("lastName", e.target.value)}
        />
      </Field>

      <Field label="Имя" error={errors.firstName}>
        <TextInput
          placeholder="Имя"
          inputClass={`${inputClass} ${errors.firstName ? "border-red-500" : ""}`}
          value={fields.firstName}
          onChange={(e) => setField("firstName", e.target.value)}
        />
      </Field>

      <Field label="Отчество (не обязательно)">
        <TextInput
          placeholder="Отчество"
          inputClass={inputClass}
          value={fields.middleName}
          onChange={(e) => setField("middleName", e.target.value)}
        />
      </Field>

      <label className={inputLabelClass}>Дата рождения: </label>
      <BirthDatePicker
          selected={fields.birthDate}
          inputValue={fields.birthDate}
          setInputValue={(val) => setField("birthDate", val)}
          inputClass={`${inputClass} ${errors.birthDate ? "border-red-500" : ""}`}
      />
      {errors.birthDate && <span className="text-red-500 text-sm">{errors.birthDate}</span>}

      <Field label="Пол" error={errors.gender}>
        <GenderSelector gender={fields.gender} setGender={(val) => setField("gender", val)} />
      </Field>

      <Field label="Телефон" error={errors.phone}>
        <PhoneInput
          phone={fields.phone}
          setPhone={(val) => setField("phone", val)}
          inputClass={`${inputNumberClass} ${errors.phone ? "border-red-500" : ""}`}
        />
      </Field>

      <Field label="Специализация" error={errors.selectedJobs}>
        <JobsList
          value={fields.selectedJobs}
          setValue={(val) => setField("selectedJobs", val)}
          inputClass={`${inputNumberClass} ${errors.selectedJobs ? "border-red-500" : ""}`}
        />
      </Field>

      <Field label="Город" error={errors.selectedCity}>
        <CitySelect
          value={fields.selectedCity}
          setValue={(val) => setField("selectedCity", val)}
          inputClass={`${inputNumberClass} ${errors.selectedCity ? "border-red-500" : ""}`}
        />
      </Field>

      <Field label="Налоговый статус" error={errors.taxStatus}>
        <TaxStatusSelect
          value={fields.taxStatus}
          setValue={(val) => setField("taxStatus", val)}
          inputClass={`${inputNumberClass} ${errors.taxStatus ? "border-red-500" : ""}`}
        />
      </Field>

      <Field label="Фото (не обязательно)" error={errors.file}>
        <PhotoUploader
          inputClass={`${inputNumberClass} ${errors.file ? "border-red-500" : ""}`}
          file={fields.file}
          setFile={(val) => setField("file", val)}
          setServerUrl={setServerUrl} // <- добавили сюда
          telegramId={telegramId}
        />
      </Field>

      <AdditionalQuestions selected={fields.selected} setSelected={(val) => setField("selected", val)} />

      <button type="submit" className="bg-blue-500 text-white p-2 rounded mt-4">
        Зарегистрироваться
      </button>
      {notification && (
        <div className="mt-2 p-2 bg-green-100 text-green-800 rounded">
          {notification}
        </div>
      )}
    </form>
  );
}

// 📦 Маленький компонент-обёртка для лейбла и ошибки
function Field({ label, error, children }) {
  return (
    <div className="mb-3 text-left">
      <label className={inputLabelClass}>{label}:</label>
      {children}
      {error && <span className="text-red-500 text-sm">{error}</span>}
    </div>
  );
}

export default App;
